import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useLocalEquityPriceDisplays } from '../../hooks/useEquityPriceDisplays.js'
import { AssetPriceSessionContext } from '../../lib/marketData/assetPriceSessionContext.js'
import { readPositionAssetListState, readPositionAssetListView, assetListViewKey } from '../../lib/positionAssetListStore.js'
import { ibkrQuoteSnapshotStore } from '../../lib/marketData/ibkrQuoteSnapshotStore.js'
import { ibkrInstrumentQuoteStore } from '../../lib/marketData/ibkrInstrumentQuoteStore.js'
import { subscribeBrowserResume } from '../../lib/marketData/browserResume.js'
import { parseOCC } from '../../lib/occParser.js'

export function SignedInAssetPriceSession({ children }) {
  const [views, setViews] = useState({})
  const [lists, setLists] = useState([])
  const revisions = useRef(new Map())
  const updateView = useCallback((key, rows) => {
    revisions.current.set(key, (revisions.current.get(key) || 0) + 1)
    setViews(previous => ({ ...previous, [key]: rows }))
  }, [])
  useEffect(() => {
    let cancelled = false, busy = false
    const refresh = async () => {
      if (busy || cancelled) return
      busy = true
      try {
        const state = await readPositionAssetListState()
        if (cancelled) return
        setLists(state.lists)
        const refs = [{ kind: 'all-assets' }, ...['active','working','ideas','recently-closed'].map(lifecycle => ({kind:'lifecycle',lifecycle})),
          ...state.lists.map(list => ({ kind:'manual-list',listId:list.listId }))]
        const keys = new Set(refs.map(assetListViewKey))
        setViews(previous => Object.fromEntries(Object.entries(previous).filter(([key]) => keys.has(key))))
        // Bounded reads; a failed list must not prevent siblings from warming.
        let index = 0
        await Promise.all([0,1].map(async () => {
          while (!cancelled && index < refs.length) {
            const ref = refs[index++], key = assetListViewKey(ref)
            const revision = revisions.current.get(key) || 0
            try {
              const rows = await readPositionAssetListView(ref)
              if (!cancelled && revision === (revisions.current.get(key) || 0)) setViews(previous => ({ ...previous, [key]: rows }))
            } catch { /* keep existing membership during a transient read failure */ }
          }
        }))
      } catch { /* retry on resume or the next refresh */ }
      finally { busy = false }
    }
    refresh()
    const timer = setInterval(refresh, 60_000)
    const stopResume = subscribeBrowserResume(refresh)
    return () => { cancelled = true; clearInterval(timer); stopResume() }
  }, [])
  const rows = useMemo(() => [...new Map(Object.values(views).flat().map(row => [row.instrumentId || row.symbol, row])).values()], [views])
  const displays = useLocalEquityPriceDisplays(rows)
  const instrumentsKey = [...new Set(rows.map(row => row.instrumentId).filter(Boolean))].sort().join('|')
  const optionsKey = [...new Set(rows.filter(row => !row.instrumentId).map(row => row.symbol).filter(symbol => parseOCC(symbol)))].sort().join('|')
  useEffect(() => {
    const releases = instrumentsKey.split('|').filter(Boolean).map(id => ibkrInstrumentQuoteStore.retain(id))
    for (const symbol of optionsKey.split('|').filter(Boolean)) {
      const occ = parseOCC(symbol)
      releases.push(ibkrQuoteSnapshotStore.retain(occ.underlying, {secType:'OPT', expiry:occ.expiry, strike:String(occ.strike), right:occ.right}))
    }
    return () => releases.forEach(release => release?.())
  }, [instrumentsKey, optionsKey])
  const instrumentsBySymbol = useMemo(() => Object.fromEntries(rows.filter(row => row.instrumentId).map(row => [String(row.symbol).toUpperCase(), row.instrumentId])), [rows])
  const ready = Object.hasOwn(views, 'all-assets')
  const value = useMemo(() => ({ views, lists, displays, updateView, instrumentsBySymbol, ready }), [views, lists, displays, updateView, instrumentsBySymbol, ready])
  return <AssetPriceSessionContext.Provider value={value}>{children}</AssetPriceSessionContext.Provider>
}

export default function AssetPriceSession({ children }) {
  const { user, loading, biometricPending } = useAuth()
  return user?.id && !loading && !biometricPending
    ? <SignedInAssetPriceSession key={user.id}>{children}</SignedInAssetPriceSession>
    : children
}
